import { GoogleGenAI, Type } from '@google/genai';
import {
  AppState,
  VariableDefinition,
  GeneratedFiles,
  Tool,
  ValidationResult,
  FormattingIssue,
} from '../types';
import { parseVariablesLocally } from './localParserService';
import { validateHCLFormatting } from '../utils/hclValidator';

// Helper for conditional logging (only in development)
const isDev = import.meta.env.DEV;
// eslint-disable-next-line @typescript-eslint/no-explicit-any, no-console
const log = (...args: any[]) => isDev && console.log(...args);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const logError = (...args: any[]) => console.error(...args); // Always log errors

/**
 * FALLBACK STRATEGY:
 * This service uses a resilient hybrid approach to ensure the app works even when Gemini API is unavailable.
 *
 * For variable parsing:
 * - Attempts Gemini API first for accuracy with complex HCL
 * - Falls back to local regex parser if API fails or returns empty results
 *
 * For file generation:
 * - Attempts Gemini API first for smart, context-aware configurations
 * - Falls back to local template-based generation if API fails
 *
 * This ensures 100% uptime - the app never stops working due to API issues.
 */

// Helper to get AI instance lazily
const getAI = () => {
  // Detect if we're in production by checking if API_KEY env var is defined
  const apiKeyFromEnv = import.meta.env.VITE_API_KEY || process.env.API_KEY;

  // Use proxy when no API key is available (production mode)
  const shouldUseProxy = !apiKeyFromEnv;
  const apiKey = apiKeyFromEnv || 'proxy-will-handle-this';

  log('🔧 [AI Config]', {
    hasApiKey: !!apiKeyFromEnv,
    shouldUseProxy,
    apiKey: apiKey.substring(0, 20) + '...',
    willUseProxy: shouldUseProxy,
    importMetaEnv: import.meta.env.MODE,
  });

  // The @google/genai SDK doesn't support custom baseUrl in the constructor
  // We need to intercept at a lower level or use fetch directly
  // For now, create the AI instance and we'll handle proxy at the fetch level
  return new GoogleGenAI({
    apiKey,
  });
};

// Helper to determine if we should use proxy
const shouldUseProxy = () => {
  const apiKeyFromEnv = import.meta.env.VITE_API_KEY || process.env.API_KEY;
  return !apiKeyFromEnv;
};

// Direct fetch call to Gemini API through proxy
const callGeminiViaProxy = async (
  model: string,
  prompt: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  responseSchema: any,
  responseMimeType: string = 'application/json'
) => {
  const endpoint = `/api-proxy/v1beta/models/${model}:generateContent`;

  log(`🔗 [Proxy Call] POST ${endpoint}`);

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      contents: [{ role: 'user', parts: [{ text: prompt }] }],
      generationConfig: {
        responseMimeType,
        responseSchema,
      },
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Proxy request failed: ${response.status} ${errorText}`);
  }

  const data = await response.json();
  log('✅ [Proxy Call] Response received');

  // Extract text from response
  const rawText = data.candidates?.[0]?.content?.parts?.[0]?.text || '';

  // The API returns JSON as a string - parse it to get actual object with proper formatting
  // When we parse, escaped \n becomes actual newlines
  return rawText;
};

/**
 * Parses raw variables.tf content to extract variable definitions using a hybrid approach.
 * 1. Tries to parse using local regex for speed and standard cases.
 * 2. Falls back to Gemini API for complex HCL or edge cases.
 */
export const parseVariablesWithGemini = async (rawHcl: string): Promise<VariableDefinition[]> => {
  log('🔍 [Variable Parsing] Starting hybrid parse...');

  // Attempt local parsing first (FAST)
  const localVariables = parseVariablesLocally(rawHcl);
  if (localVariables.length > 0) {
    log('Using locally parsed variables');
    return localVariables;
  }

  // Fallback to Gemini (SLOW but SMART)
  log('Fallback to Gemini for variable parsing');
  try {
    const model = 'gemini-2.0-flash-exp';

    const prompt = `
      You are an expert Terraform HCL parser. Your task is to analyze the provided Terraform configuration content (typically from variables.tf) and extract all variable definitions with high precision.

      CRITICAL INSTRUCTIONS:
      1. Identify every 'variable "name" { ... }' block.
      2. Extract:
         - name: The variable name.
         - type: The explicit type definition (e.g., 'string', 'list(string)', 'object({ name = string })'). If missing, infer 'any' or the type of the default value.
         - description: The description string. If missing, return empty string.
         - default: The default value as a raw HCL string. e.g., 'true', '10', '["us-east-1"]', '{ foo = "bar" }'.
           - IMPORTANT: If the default value is a complex object or list, preserve its HCL structure in the string representation.
           - If there is NO default argument in the block, the variable is required.
      3. Edge Cases:
         - Handle multiline strings in defaults (heredoc).
         - Handle comments inside the variable block (ignore them).
         - Handle complex nested object types.
         - Handle 'nullable' fields if present.

      Input Content:
      ${rawHcl.substring(0, 40000)} 
    `;

    const responseSchema = {
      type: Type.ARRAY,
      items: {
        type: Type.OBJECT,
        properties: {
          name: { type: Type.STRING },
          type: { type: Type.STRING, description: 'The type definition of the variable.' },
          description: { type: Type.STRING },
          default: {
            type: Type.STRING,
            description: 'The default value as a string. Null if no default.',
          },
          required: { type: Type.BOOLEAN },
        },
        required: ['name', 'type', 'required'],
      },
    };

    let text;
    if (shouldUseProxy()) {
      // Use fetch-based proxy call
      text = await callGeminiViaProxy(model, prompt, responseSchema);
    } else {
      // Use SDK directly
      const ai = getAI();
      const response = await ai.models.generateContent({
        model,
        contents: prompt,
        config: {
          responseMimeType: 'application/json',
          responseSchema,
        },
      });
      text = response.text;
    }

    if (!text) {
      log('Gemini returned empty response, using local parser');
      return parseVariablesLocally(rawHcl);
    }

    // Parse JSON
    const parsed = JSON.parse(text) as VariableDefinition[];

    // Validate we got actual results
    if (!Array.isArray(parsed) || parsed.length === 0) {
      log('⚠️  [Variable Parsing] Gemini returned no variables, using local parser');
      return parseVariablesLocally(rawHcl);
    }

    log(`✅ [Variable Parsing] Successfully parsed ${parsed.length} variables using Gemini API`);
    return parsed;
  } catch (error) {
    logError('❌ [Variable Parsing] Gemini API Error:', error);
    log('🔄 [Variable Parsing] Falling back to local regex-based parser...');

    // Always fallback to local parsing on any error
    const fallbackVars = parseVariablesLocally(rawHcl);
    log(`✅ [Variable Parsing] Local parser found ${fallbackVars.length} variables`);
    return fallbackVars;
  }
};

/**
 * Local Regex-based parser for standard HCL variable blocks.
 * DEPRECATED: Use parseVariablesLocally from localParserService.ts instead
 * This is kept for reference but should not be called directly
 */

/**
 * Local template-based generator for when Gemini API is unavailable.
 * Generates basic but functional Terraform/Terragrunt files.
 */
const generateFilesLocally = (state: AppState): GeneratedFiles => {
  log('🏠 [Local Generator] Using template-based generation (no AI)');
  const isTerragrunt = state.tool === Tool.TERRAGRUNT;
  const overridesContent = state.stagedOverrides
    .map((o) => {
      // Format value based on type
      let formattedValue = o.value;
      if (typeof o.value === 'string' && !o.value.startsWith('"')) {
        formattedValue = `"${o.value}"`;
      } else if (typeof o.value === 'boolean') {
        formattedValue = String(o.value);
      }

      return `variable "${o.name}" {
  default = ${formattedValue}
}`;
    })
    .join('\n\n');

  if (isTerragrunt) {
    // Terragrunt pattern
    const provider =
      state.provider === 'aws'
        ? `provider "aws" {
  region = "${state.region}"
}`
        : state.provider === 'gcp'
          ? `provider "google" {
  project = "${state.gcpProjectId || 'YOUR_PROJECT_ID'}"
  region  = "${state.region}"
}`
          : `provider "${state.provider}" {
  region = "${state.region}"
}`;

    const main = `terraform {
  source = "${state.moduleUrl}"
  
  extra_arguments "always_local_plan" {
    commands  = ["plan"]
    arguments = ["-out=./saved-plan"]
  }

  extra_arguments "force_saved_plan" {
    commands  = ["apply"]
    arguments = ["./saved-plan"]
  }
}`;

    return {
      provider,
      main,
      overrides: overridesContent,
    };
  } else {
    // Terraform/OpenTofu wrapper module pattern
    const provider =
      state.provider === 'aws'
        ? `terraform {
  required_providers {
    aws = {
      source  = "hashicorp/aws"
      version = "~> 5.0"
    }
  }
}

provider "aws" {
  region = "${state.region}"
}`
        : state.provider === 'gcp'
          ? `terraform {
  required_providers {
    google = {
      source  = "hashicorp/google"
      version = "~> 5.0"
    }
  }
}

provider "google" {
  project = "${state.gcpProjectId || 'YOUR_PROJECT_ID'}"
  region  = "${state.region}"
}`
          : `terraform {
  required_providers {
    ${state.provider} = {
      source = "hashicorp/${state.provider}"
    }
  }
}

provider "${state.provider}" {
  region = "${state.region}"
}`;

    const variableMappings = state.stagedOverrides
      .map((o) => `  ${o.name} = var.${o.name}`)
      .join('\n');

    const main = `module "this" {
  source = "${state.moduleUrl}"
  
${variableMappings}
}`;

    return {
      provider,
      main,
      overrides: overridesContent,
    };
  }
};

/**
 * Generates the final Terraform/Terragrunt files based on the user's configuration.
 * Falls back to local template generation if Gemini API is unavailable.
 */
export const generateTerraformFiles = async (state: AppState): Promise<GeneratedFiles> => {
  log('📝 [File Generation] Starting Terraform file generation...');
  try {
    const model = 'gemini-2.5-flash';
    const isTerragrunt = state.tool === Tool.TERRAGRUNT;
    const toolName =
      state.tool === Tool.OPENTOFU ? 'OpenTofu' : isTerragrunt ? 'Terragrunt' : 'Terraform';
    log(`🤖 [File Generation] Attempting Gemini API generation for ${toolName}...`);

    let instructions = '';

    if (isTerragrunt) {
      instructions = `
      Please generate the following configuration files for a Terragrunt implementation.
      
      1. 'provider': Content for a standalone 'providers.tf' file.
         - Should contain the provider configuration block (e.g., provider "aws" { ... }).
      
      2. 'main': Content for 'terragrunt.hcl'.
         - STRICT REQUIREMENT: Structure MUST be:
           terraform {
             source = "${state.moduleUrl}"
             
             extra_arguments "always_local_plan" {
               commands  = ["plan"]
               arguments = ["-out=./saved-plan"]
             }

             extra_arguments "force_saved_plan" {
               commands  = ["apply"]
               arguments = ["./saved-plan"]
             }
           }
         - DO NOT include an 'inputs' block.
         - Ensure strictly valid HCL syntax with 2-space indentation.
         
      3. 'overrides': Content for 'overrides.tf'.
         - This file is used to override the default values of the variables in the remote module.
         - For each overridden variable, define a 'variable' block with the 'default' set to the user's value.
         - Example:
           variable "instance_type" {
             default = "t3.micro"
           }
      `;
    } else {
      // Terraform and OpenTofu - Wrapper Module Pattern
      instructions = `
      Please generate the following configuration files for a standard ${toolName} implementation using a "Wrapper Module" pattern.

      1. 'provider': Content for 'provider.tf'.
         - Include the required providers block and the provider configuration.

      2. 'main': Content for 'main.tf'.
         - Instantiates the module found at "${state.moduleUrl}".
         - YOU MUST pass every variable defined in 'overrides.tf' to this module using the 'key = var.key' syntax.
         - Example:
           module "this" {
             source = "${state.moduleUrl}"
             
             # Mappings
             create = var.create
             instance_type = var.instance_type
           }
         - STRICT HCL FORMATTING: 
           - Use 'key = value' syntax.
           - Do NOT use colons (:).
           - Do NOT use JSON syntax.

      3. 'overrides': Content for 'overrides.tf'.
         - This file acts as the variable definition file for the wrapper module.
         - For each user override, define a 'variable' block with the 'default' set to the user's value.
         - Example:
           variable "create" {
             default = true
           }
           variable "instance_type" {
             default = "t3.micro"
           }
      `;
    }

    const prompt = `
    You are an expert DevOps engineer specializing in ${toolName}.
    
    TASK: Generate valid HCL configuration files.
    
    CONTEXT:
    Cloud Provider: ${state.provider}
    Region: ${state.region}
    GCP Project ID: ${state.gcpProjectId}
    Module Source: ${state.moduleUrl}
    
    OVERRIDES (User defined variables):
    ${state.stagedOverrides.map((o) => `${o.name} = ${o.value}`).join('\n')}
    
    ${instructions}

    CRITICAL FORMATTING REQUIREMENTS: 
    - Return valid, properly formatted HCL code with newlines and indentation
    - Each HCL block should be on separate lines with proper indentation (2 spaces)
    - Include newline characters (\\n) between statements and blocks
    - The JSON strings MUST contain properly formatted HCL, not single-line compressed code
    - No Markdown formatting (like \`\`\`) inside the JSON strings
    - No explanation text
    
    Example of CORRECT formatting in JSON response:
    {
      "provider": "terraform {\\n  required_providers {\\n    aws = {\\n      source = \\"hashicorp/aws\\"\\n    }\\n  }\\n}\\n\\nprovider \\"aws\\" {\\n  region = \\"us-east-1\\"\\n}"
    }
    `;

    const responseSchema = {
      type: Type.OBJECT,
      properties: {
        provider: {
          type: Type.STRING,
          description: isTerragrunt ? 'Content for providers.tf' : 'Content for provider.tf',
        },
        main: {
          type: Type.STRING,
          description: isTerragrunt ? 'Content for terragrunt.hcl' : 'Content for main.tf',
        },
        overrides: { type: Type.STRING, description: 'Content for overrides.tf' },
      },
      required: ['provider', 'main', 'overrides'],
    };

    let text;
    if (shouldUseProxy()) {
      // Use fetch-based proxy call
      text = await callGeminiViaProxy(model, prompt, responseSchema);
    } else {
      // Use SDK directly
      const ai = getAI();
      const response = await ai.models.generateContent({
        model,
        contents: prompt,
        config: {
          responseMimeType: 'application/json',
          responseSchema,
        },
      });
      text = response.text;
    }

    if (!text) throw new Error('No content generated');

    const files = JSON.parse(text) as GeneratedFiles;

    // Debug: Check if newlines are preserved
    log('📄 [File Generation] Sample of generated provider.tf:', files.provider.substring(0, 100));
    log('✅ [File Generation] Successfully generated files using Gemini API');
    return files;
  } catch (error) {
    logError('❌ [File Generation] Gemini API Error:', error);
    log('🔄 [File Generation] Falling back to local template generation...');

    // Return locally generated files as fallback
    const files = generateFilesLocally(state);
    log('✅ [File Generation] Successfully generated files using local templates');
    return files;
  }
};

/**
 * Local validation fallback - performs basic HCL syntax checks without AI.
 * Checks for common issues like unmatched braces, invalid block structures, formatting, etc.
 */
const validateLocally = (files: GeneratedFiles, tool: Tool): ValidationResult => {
  log('🏠 [Local Validation] Using local syntax validation (no AI)');
  const issues: string[] = [];
  const formattingIssues: FormattingIssue[] = [];
  const allContent = `${files.provider}\n\n${files.main}\n\n${files.overrides}`;

  // Check formatting for each file
  const providerFormatIssues = validateHCLFormatting(files.provider, 'provider');
  const mainFormatIssues = validateHCLFormatting(files.main, 'main');
  const overridesFormatIssues = validateHCLFormatting(files.overrides, 'overrides');

  formattingIssues.push(...providerFormatIssues, ...mainFormatIssues, ...overridesFormatIssues);

  // Check 1: Balanced braces
  let braceCount = 0;
  let lineNum = 0;
  for (const line of allContent.split('\n')) {
    lineNum++;
    for (const char of line) {
      if (char === '{') braceCount++;
      if (char === '}') braceCount--;
      if (braceCount < 0) {
        issues.push(`Line ${lineNum}: Unmatched closing brace '}'`);
        break;
      }
    }
  }
  if (braceCount > 0) {
    issues.push(`Unmatched opening braces detected (missing ${braceCount} closing brace(s))`);
  } else if (braceCount < 0) {
    issues.push(`Extra closing braces detected`);
  }

  // Check 2: Basic HCL block structure validation
  const invalidBlocks = allContent.match(
    /\b(resource|provider|module|variable|terraform|locals|data|output)\s+[^{"\s]/g
  );
  if (invalidBlocks && invalidBlocks.length > 0) {
    issues.push(`Potentially invalid block syntax: ${invalidBlocks.join(', ')}`);
  }

  // Check 3: Empty required blocks
  if (files.provider.trim().length === 0) {
    issues.push('Provider configuration is empty');
  }
  if (files.main.trim().length === 0) {
    issues.push('Main configuration is empty');
  }

  // Check 4: Check for common typos in keywords
  const commonTypos = [
    { typo: /\bprovidor\b/gi, correct: 'provider' },
    { typo: /\bmodule\s*=/gi, correct: 'module "name"' },
    { typo: /\bresource\s*=/gi, correct: 'resource "type" "name"' },
  ];
  for (const { typo, correct } of commonTypos) {
    if (typo.test(allContent)) {
      issues.push(`Possible typo detected - did you mean '${correct}'?`);
    }
  }

  // Check 5: Tool-specific validation
  if (tool === Tool.TERRAGRUNT) {
    if (!files.main.includes('terraform')) {
      issues.push('Terragrunt configuration missing terraform block');
    }
    if (!files.main.includes('source')) {
      issues.push('Terragrunt configuration missing source parameter');
    }
  } else {
    // Terraform/OpenTofu
    if (files.main.includes('module') && !files.main.includes('source')) {
      issues.push('Module block missing required "source" parameter');
    }
  }

  // Generate summary
  const hasFormattingIssues = formattingIssues.length > 0;
  const hasSyntaxIssues = issues.length > 0;
  const isValid = !hasSyntaxIssues; // Valid if no syntax issues (formatting issues are warnings)

  let summary = '';

  if (isValid && !hasFormattingIssues) {
    summary = `✅ Local validation passed! All syntax checks and formatting validation completed successfully.

📋 Validation Summary:
• Syntax: All braces balanced, block structures valid
• Formatting: Code follows HCL formatting standards
• Structure: All required blocks present

Note: This is a basic local validation. For comprehensive validation including resource predictions and cloud-specific checks, the Gemini API provides deeper analysis.

✓ Ready to apply: Review the configuration and proceed with confidence.`;
  } else if (isValid && hasFormattingIssues) {
    summary = `⚠️ Syntax valid but formatting issues detected.

📋 Validation Summary:
• Syntax: ✓ Passed (all braces balanced, structures valid)
• Formatting: ${formattingIssues.length} formatting issue(s) found

The configuration is syntactically correct but doesn't follow HCL formatting standards. Run \`terraform fmt\` to auto-fix these issues.

Action: Review formatting suggestions in the detailed report below.`;
  } else {
    summary = `❌ Validation detected ${issues.length} syntax issue(s)${hasFormattingIssues ? ` and ${formattingIssues.length} formatting issue(s)` : ''}.

📋 Validation Summary:
• Syntax: ${issues.length} error(s) - must be fixed
• Formatting: ${hasFormattingIssues ? formattingIssues.length + ' warning(s)' : '✓ Passed'}

Note: This is a basic local validation. Some issues may be false positives. For more accurate validation, ensure the Gemini API is available.

Action Required: Fix the reported syntax errors before applying the configuration.`;
  }

  log(
    `✅ [Local Validation] Completed with ${issues.length} syntax issue(s) and ${formattingIssues.length} formatting issue(s)`
  );
  return {
    isValid,
    issues,
    summary,
    formattingIssues,
    hasFormattingIssues,
  };
};

/**
 * Validates generated Terraform/Terragrunt configuration files.
 * Performs syntax checking and simulates a dry run to detect potential issues.
 * Falls back to local validation if Gemini API is unavailable.
 */
export const validateConfiguration = async (
  files: GeneratedFiles | null,
  tool: Tool
): Promise<ValidationResult> => {
  if (!files) {
    return {
      isValid: false,
      issues: ['No files generated yet'],
      summary: 'Please generate files first before validating.',
    };
  }

  try {
    log('🔍 [Validation] Starting configuration validation...');
    const model = 'gemini-2.5-flash';
    const toolName =
      tool === Tool.TERRAGRUNT ? 'Terragrunt' : tool === Tool.OPENTOFU ? 'OpenTofu' : 'Terraform';

    const prompt = `
      You are an expert ${toolName} engineer.
      
      TASK: Review the following generated configuration files for syntax validity and simulate a "Dry Run".
      
      FILE: provider.tf / providers.tf
      ${files.provider}
      
      FILE: main.tf / terragrunt.hcl
      ${files.main}
      
      FILE: overrides.tf
      ${files.overrides}
      
      INSTRUCTIONS:
      1. Syntax Check: Check for HCL syntax errors, deprecated attributes, or invalid block structures.
      2. Dry Run Simulation: Imagine running \`${tool} plan\`. Predict what resources would be created/modified/destroyed.
      3. Report Issues: List any problems you detect (e.g., missing required variables, invalid references, syntax errors).
      
      RESPONSE FORMAT:
      {
        "isValid": boolean,
        "issues": ["issue 1", "issue 2", ...] or [] if no issues,
        "summary": "A concise summary of the validation result and predicted plan output"
      }
      
      Be thorough but concise. Focus on actionable feedback.
    `;

    const responseSchema = {
      type: Type.OBJECT,
      properties: {
        isValid: { type: Type.BOOLEAN },
        issues: {
          type: Type.ARRAY,
          items: { type: Type.STRING },
        },
        summary: { type: Type.STRING },
      },
      required: ['isValid', 'issues', 'summary'],
    };

    let text;
    if (shouldUseProxy()) {
      text = await callGeminiViaProxy(model, prompt, responseSchema);
    } else {
      const ai = getAI();
      const response = await ai.models.generateContent({
        model,
        contents: prompt,
        config: {
          responseMimeType: 'application/json',
          responseSchema,
        },
      });
      text = response.text;
    }

    if (!text) throw new Error('No validation response generated');

    const result = JSON.parse(text) as ValidationResult;
    log('✅ [Validation] Validation completed:', result.isValid ? 'PASSED' : 'ISSUES FOUND');
    return result;
  } catch (error) {
    logError('❌ [Validation] Gemini API Error:', error);
    log('🔄 [Validation] Falling back to local validation...');

    // Return local validation as fallback
    const localResult = validateLocally(files, tool);
    log('✅ [Validation] Successfully completed local validation');
    return localResult;
  }
};

// Export formatter utility for UI use
export { formatHCL } from '../utils/hclValidator';
